import React, { useState, useEffect } from "react";
import { MdAccountBalance, MdToday } from "react-icons/md";
import DepenseServiceInstance from "service/depense.service";
import HeaderSection from "components/headers-section";
import ActionBar from "components/action-bar";
import DepenseTable from "./components/DepenseTable";
import DepenseFormModal from "./components/DepenseFormModal";
import DeleteConfirmationModal from "components/delete-confirm-modal";
import DepenseFilters from "./components/DepenseFilters";

const DepenseManager = () => {
  const [depenses, setDepenses] = useState([]);
  const [filteredDepenses, setFilteredDepenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentDepense, setCurrentDepense] = useState({
    designation: "",
    qte_depense: 1,
    pu_depense: "",
    desc_depense: "",
    date_depense: new Date().toISOString().split("T")[0],
  });
  const [isEditing, setIsEditing] = useState(false);
  const [filters, setFilters] = useState({
    dateStart: null,
    dateEnd: null,
    minAmount: "",
    maxAmount: "",
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchDepenses();
  }, []);

  useEffect(() => {
    filterDepenses();
  }, [searchTerm, depenses, filters]);

  const fetchDepenses = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await DepenseServiceInstance.getAll();
      setDepenses(result || []);
      setFilteredDepenses(result || []);
    } catch (error) {
      setError(
        "Impossible de charger les dépenses. Veuillez réessayer plus tard."
      );
      console.error("Error fetching dépenses:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterDepenses = () => {
    let filtered = [...depenses];

    // Recherche textuelle
    if (searchTerm) {
      filtered = filtered.filter(
        (depense) =>
          depense.designation
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          depense.desc_depense.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtre de date
    if (filters.dateStart) {
      filtered = filtered.filter(
        (depense) =>
          new Date(depense.date_depense) >= new Date(filters.dateStart)
      );
    }

    if (filters.dateEnd) {
      filtered = filtered.filter(
        (depense) => new Date(depense.date_depense) <= new Date(filters.dateEnd)
      );
    }

    // Filtre de montant
    const minAmount = parseFloat(filters.minAmount);
    if (!isNaN(minAmount)) {
      filtered = filtered.filter((depense) => {
        const total =
          parseFloat(depense.pu_depense) * parseFloat(depense.qte_depense);
        return total >= minAmount;
      });
    }

    const maxAmount = parseFloat(filters.maxAmount);
    if (!isNaN(maxAmount)) {
      filtered = filtered.filter((depense) => {
        const total =
          parseFloat(depense.pu_depense) * parseFloat(depense.qte_depense);
        return total <= maxAmount;
      });
    }

    setFilteredDepenses(filtered);
  };

  const openCreateModal = () => {
    setCurrentDepense({
      designation: "",
      qte_depense: 1,
      pu_depense: "",
      desc_depense: "",
      date_depense: new Date().toISOString().split("T")[0],
    });
    setIsEditing(false);
    setIsFormModalOpen(true);
  };

  const openEditModal = (depense) => {
    setCurrentDepense({
      id_caisse: depense.id_caisse,
      designation: depense.designation,
      qte_depense: depense.qte_depense,
      pu_depense: depense.pu_depense,
      desc_depense: depense.desc_depense,
      date_depense: depense.date_depense,
    });
    setIsEditing(true);
    setIsFormModalOpen(true);
  };

  const openDeleteModal = (depense) => {
    setCurrentDepense(depense);
    setIsDeleteModalOpen(true);
  };

  const handleSubmitForm = async (formData) => {
    setLoading(true);
    try {
      if (isEditing) {
        await DepenseServiceInstance.update(currentDepense.id_caisse, formData);
      } else {
        await DepenseServiceInstance.create(formData);
      }

      fetchDepenses();
      setIsFormModalOpen(false);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        errors: error.response?.data?.errors || {
          general: isEditing
            ? "Impossible de modifier la dépense. Veuillez réessayer."
            : "Impossible de créer la dépense. Veuillez réessayer.",
        },
      };
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await DepenseServiceInstance.delete(currentDepense.id_caisse);
      fetchDepenses();
      setIsDeleteModalOpen(false);
    } catch (error) {
      setError("Impossible de supprimer la dépense. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
  };

  const resetFilters = () => {
    setFilters({
      dateStart: null,
      dateEnd: null,
      minAmount: "",
      maxAmount: "",
    });
    setSearchTerm("");
  };

  // Calculer les statistiques des dépenses filtrées
  const calculateStats = () => {
    const total = filteredDepenses.reduce((acc, dep) => {
      return acc + parseFloat(dep.pu_depense) * parseFloat(dep.qte_depense);
    }, 0);

    const count = filteredDepenses.length;

    let minDate = null;
    let maxDate = null;

    if (count > 0) {
      minDate = new Date(
        Math.min(...filteredDepenses.map((d) => new Date(d.date_depense)))
      );
      maxDate = new Date(
        Math.max(...filteredDepenses.map((d) => new Date(d.date_depense)))
      );
    }

    return {
      total,
      count,
      minDate,
      maxDate,
    };
  };

  const stats = calculateStats();

  return (
    <div className="min-h-screen bg-gray-50 px-5 py-8 md:px-8">
      <HeaderSection
        title="Gestion des Dépenses"
        description="Enregistrez et suivez toutes vos dépenses"
        accentColor="#57A863"
      />

      {/* Statistiques */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-4">
        <div className="rounded-md border border-blue-200 bg-blue-50 p-4">
          <h3 className="text-sm font-medium text-blue-900">
            Total des dépenses
          </h3>
          <p className="mt-2 text-xl font-bold text-blue-700">
            {new Intl.NumberFormat("fr-MG", {
              style: "currency",
              currency: "MGA",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(stats.total)}
          </p>
        </div>

        <div className="rounded-md border border-green-200 bg-green-50 p-4">
          <h3 className="text-sm font-medium text-green-900">
            Nombre de dépenses
          </h3>
          <p className="mt-2 text-xl font-bold text-green-700">{stats.count}</p>
        </div>

        <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
          <h3 className="text-sm font-medium text-amber-900">
            Première dépense
          </h3>
          <p className="mt-2 text-xl font-bold text-amber-700">
            {stats.minDate
              ? new Intl.DateTimeFormat("fr-FR").format(stats.minDate)
              : "Aucune"}
          </p>
        </div>

        <div className="rounded-md border border-purple-200 bg-purple-50 p-4">
          <h3 className="text-sm font-medium text-purple-900">
            Dernière dépense
          </h3>
          <p className="mt-2 text-xl font-bold text-purple-700">
            {stats.maxDate
              ? new Intl.DateTimeFormat("fr-FR").format(stats.maxDate)
              : "Aucune"}
          </p>
        </div>
      </div>

      <ActionBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Rechercher une dépense..."
        searchIcon={MdAccountBalance}
        onRefresh={fetchDepenses}
        onCreateNew={openCreateModal}
        buttonLabel="Nouvelle Dépense"
        loading={loading}
        primaryColor="#57A863"
        hoverColor="#4c9657"
        customActions={
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 rounded-md bg-gray-100 px-3 py-2 text-gray-700 transition-colors hover:bg-gray-200"
            title={showFilters ? "Masquer les filtres" : "Afficher les filtres"}
          >
            <MdToday className="h-5 w-5" />
            <span>Filtres</span>
            <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs">
              {
                Object.values(filters).filter((v) => v !== null && v !== "")
                  .length
              }
            </span>
          </button>
        }
      />

      {showFilters && (
        <DepenseFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={resetFilters}
        />
      )}

      {error && (
        <div
          className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600"
          role="alert"
        >
          {error}
        </div>
      )}

      <DepenseTable
        depenses={filteredDepenses}
        loading={loading}
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />
      <DepenseFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        onSubmit={handleSubmitForm}
        initialDepense={currentDepense}
        isEditing={isEditing}
        loading={loading}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        item={currentDepense}
        itemName={currentDepense.designation}
        itemType="dépense"
        warning="Cette opération supprimera définitivement cette dépense de la caisse."
        loading={loading}
      />
    </div>
  );
};

export default DepenseManager;
